import { clockTime, meridiem } from './attendance'

/**
 * A bonus private time: a stretch on one date added to a teacher's weekly
 * pattern (D-684, D-720, D-722).
 *
 * <b>The flexible half of pilot item 29</b> — *"stay flexible if they happen to
 * be available on one of those days"*. It only adds; a day out is time off.
 *
 * <b>Hers, with no approval.</b> What the studio agreed to is the week; one
 * Friday is not a new week, and routing it through an approval would mean she
 * could not answer a question about next Friday until Monday.
 */
export type DayChange = {
  id: string
  date: string

  /**
   * Both null only on a legacy "not in that day" row saved before D-722; new
   * rows always carry both, and the server refuses anything less.
   */
  startTime: string | null
  endTime: string | null

  /**
   * Time off covers this date, so the change does nothing until the holiday
   * goes.
   *
   * <b>Kept and marked, not deleted.</b> Booking a holiday over a change is the
   * reversible direction — remove the holiday and what she had comes back — but
   * a row sitting on her screen doing nothing is exactly what the entry door
   * refuses in the other direction, so it has to say so.
   */
  overridden: boolean
}

export async function fetchMyDayChanges(): Promise<DayChange[]> {
  const res = await fetch('/api/teacher/day-changes')

  // A parent has no staff row and so no pattern to amend; the panel does not render.
  if (res.status === 404) return []
  if (!res.ok) throw new Error(`Could not load your bonus private times (${res.status})`)

  return res.json()
}

export async function addDayChange(
  date: string,
  startTime: string | null,
  endTime: string | null,
): Promise<DayChange> {
  const res = await fetch('/api/teacher/day-changes', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ date, startTime, endTime }),
  })

  if (!res.ok) {
    // The server explains the refusal - a day gone by, a mistyped year, half a
    // pair of times, or a date her own time off already covers. Passing the
    // sentence through beats a status code, and the time-off one names the
    // remedy, which is not the row she is looking at.
    const problem = await res.json().catch(() => null)
    throw new Error(problem?.detail ?? problem?.title ?? `Could not save (${res.status})`)
  }

  return res.json()
}

export async function removeDayChange(id: string): Promise<void> {
  const res = await fetch(`/api/teacher/day-changes/${id}`, { method: 'DELETE' })

  if (!res.ok) throw new Error(`Could not remove that (${res.status})`)
}

/**
 * A change as one line: <i>Fri 2 Oct, 2:00&ndash;6:00pm</i>, or
 * <i>Fri 2 Oct, not in</i>.
 *
 * <b>UTC-anchored</b>, so it is DST-proof: these are dates rather than instants,
 * and day arithmetic on a date is pure counting (D-68, D-485).
 *
 * <b>The weekday is part of it, and that is the point of the control.</b> She is
 * amending one weekday of her pattern, so *"Fri 2 Oct"* is what tells her
 * whether she picked the day she meant — a bare date makes her count.
 */
export function changeLabel(change: DayChange): string {
  const day = new Date(`${change.date}T00:00:00Z`).toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  })

  if (!change.startTime || !change.endTime) return `${day}, not in`

  const from = clockTime(change.startTime)
  const to = clockTime(change.endTime)

  /*
   * The meridiem is printed once when both ends share it - "2:00-6:00pm" rather
   * than "2:00pm-6:00pm" - and twice when they do not, which is the morning
   * teacher's ordinary case: "9:00am-12:00pm".
   */
  return meridiem(change.startTime) === meridiem(change.endTime)
    ? `${day}, ${from}–${to}${meridiem(change.endTime)}`
    : `${day}, ${from}${meridiem(change.startTime)}–${to}${meridiem(change.endTime)}`
}

/**
 * Whether a change is in the past.
 *
 * <b>Takes today rather than reading the clock</b>, so a caller can pass the
 * app's own `today()` — the UTC date is tomorrow after about 7pm Central, which
 * is inside studio hours (D-68, D-485).
 */
export function isGone(change: DayChange, today: string): boolean {
  return change.date < today
}
